import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { FormattedMessage as T, useIntl } from "react-intl";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import { Box, Button, Dialog, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import useGlobalContext from "hooks/globalData";
import LazyTable, {
  TableCellType,
  TableSortType,
  DEFAULT_ROWS_PER_PAGE,
} from "components/LazyTable";
import {
  ResourceLibraryItem,
  IResourceLibraryItemAttribute,
  ListResponseOfResourceLibraryItemAttribute,
  ListRequestOfBaseFilter,
} from "services/KnowfullyAdminClient";
import ResourceLibraryItemTopicCreateForm from "./ResourceLibraryItemTopicCreateForm";
import SnackbarMessage from "components/SnackbarMessage";
import { SnackbarMessageProps } from "types/common-types";

interface IResourceLibraryItemAttributeWithId
  extends IResourceLibraryItemAttribute {
  id: string;
}

type ResourceLibraryItemTopicsListProps = {
  resourceLibraryItem: ResourceLibraryItem;
};

const useStyles = (theme: Theme) => ({
  rulesHeader: {
    display: "flex",
    justifyContent: "space-between",
    margin: theme.spacing(4, 0, 3, 0),
    width: "100%",
  },
});

const ResourceLibraryItemTopicsList = ({
  resourceLibraryItem,
}: ResourceLibraryItemTopicsListProps) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { resourceLibraryService } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("attribute.value");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfResourceLibraryItemAttribute>(
    [
      "resourceLibraryService.getResourceLibraryItemTopicAttributes",
      resourceLibraryItem.id,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      resourceLibraryService.getResourceLibraryItemTopicAttributes(
        resourceLibraryItem.id,
        ListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
        }),
      ),
    {
      keepPreviousData: true,
    },
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const resourceLibraryItemAttributes = data?.data as IResourceLibraryItemAttributeWithId[];

  const onDelete = (ids: string[]) => {
    ids.forEach((id) => {
      registerPromise(
        resourceLibraryService.deleteResourceLibraryItemTopicAttribute(id),
      )
        .then(() => {
          queryClient.invalidateQueries("resourceLibraryService.getResourceLibraryItemTopicAttributes");
        })
        .catch((e) => {
          console.log(e);
        });
    });
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "attributeValue",
      customSortAccessor: "attribute.value",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "resourceLibraryItemAttributeListNameLabel" }),
    },
  ];

  const onResourceLibraryItemTopicCreated = () => {
    queryClient.invalidateQueries(["resourceLibraryService.getResourceLibraryItemTopicAttributes"]);
    createOpenActions.setFalse();

    setSnackbarMessage(
      new SnackbarMessageProps(
        t({ id: "resourceLibraryItemAttributeCreated" }),
      ),
    );
  };

  return (
    <Box>
      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: resourceLibraryItemAttributes ?? [],
          loading: isFetching,

          dense: true,
          titleComponent: (
            <Box sx={styles.rulesHeader}>
              <Typography variant="h6">
                <T id="resourceLibraryItemAttributeListLabel" />
              </Typography>
              <Button
                onClick={createOpenActions.setTrue}
                startIcon={<AddIcon />}
                data-cy="addAttributeButton"
              >
                <T id="resourceLibraryFormAddItemAttributeButton" />
              </Button>
            </Box>
          ),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onDelete,
        }}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <ResourceLibraryItemTopicCreateForm
          resourceLibraryItem={resourceLibraryItem}
          onResourceLibraryItemTopicCreated={
            onResourceLibraryItemTopicCreated
          }
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default ResourceLibraryItemTopicsList;
